import { CategoryFieldSuggestionsValue } from "@coveo/headless";
import colorPallete, { bodyFonts, labelFonts, themeFontWeights } from "@DSG/MobiusOverrides/styles/variables";
import CoveoApplicationState from "@DSG/Store/Data/CoveoApplicationState/CoveoApplicationState";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const splitBrandText = (displayValue: string, query: string) => {
    if (!query) {return [displayValue];}
    const lowerDisplayValue = displayValue.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const index = lowerDisplayValue.indexOf(lowerQuery);
    if (index === -1) {return [displayValue];}

    const before = displayValue.substring(0, index);
    const match = displayValue.substring(index, index + query.length);
    const after = displayValue.substring(index + query.length);

    return [before, match, after];
};

interface OwnProps {
    closeAutocomplete: () => void;
    onBrandClick: (brand: CategoryFieldSuggestionsValue) => void;
    query: string;
}

const mapStateToProps = (state: CoveoApplicationState) => ({
    coveoCommerce: state.DSGGlobalData,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CoveoSearchBrandsStyles {
    wrapper?: InjectableCss;
    headingText?: TypographyPresentationProps;
    itemListWrapper?: InjectableCss;
    itemWrapper?: InjectableCss;
    itemLink?: LinkPresentationProps;
    boldText?: TypographyPresentationProps;
}

export const coveoSearchBrandsStyles: CoveoSearchBrandsStyles ={
    headingText: {
        variant: "h6",
        css: css`
            border-bottom: 1px solid ${colorPallete.grey300};
            padding-bottom: 8px;
            font-size: ${bodyFonts.body2RegularLg.fontSize};
            line-height: ${bodyFonts.body2RegularLg.lineHeight};
            margin: 0 24px 8px;
        `
    },
    itemWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `
    },
    itemLink: {
        css: css`
            font: ${labelFonts.label1Regular.fontWeight} ${labelFonts.label1Regular.fontSize}/${labelFonts.label1Regular.lineHeight} ${labelFonts.label1Regular.fontFamily};
            margin: 6px 24px;
            text-transform: lowercase;
            .spanLink {
                -webkit-box-orient: vertical;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            &:hover {
                [class*="StyledTypography-"] {
                    text-decoration: underline;
                }  
            }
        `
    },
    boldText: {
        css: css`
            font-weight: ${themeFontWeights.semiBold};
        `
    },
};

const styles = coveoSearchBrandsStyles;

const CoveoSearchBrands = ({
    closeAutocomplete,
    coveoCommerce,
    onBrandClick,
    query
}: Props) => {
    const [currentBrandSuggestions, setCurrentBrandSuggestions] = useState<any[]>([]);
    const lastBrandQueryRef = useRef<string | undefined>(undefined);
    const brandFilterSuggestions = coveoCommerce?.FilterSuggestionsController?.filterSuggestions?.find(
        (item: any) => item.state.field === "ec_brand"
    );

    useEffect(() => {
        if (!brandFilterSuggestions) {return;}

        if (query && lastBrandQueryRef.current !== query) {
            setTimeout(() => {
                brandFilterSuggestions.updateQuery?.(query);
                lastBrandQueryRef.current = query;
            }, 0);
        }

        const unsubscribe = brandFilterSuggestions.subscribe(() => {
            const relevantBrands = [...brandFilterSuggestions.state.values]
                .sort((a, b) => b.count - a.count)
                .slice(0, 3);

            setCurrentBrandSuggestions(relevantBrands || []);
        });

        return () => {
            if (unsubscribe) {unsubscribe();}
        };
    }, [brandFilterSuggestions, query]);

    if (!brandFilterSuggestions || currentBrandSuggestions.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} className="brandSuggestion">
            <Typography {...styles.headingText}>
                {translate("Brands")}
            </Typography>
            <StyledWrapper {...styles.itemListWrapper}>
                {currentBrandSuggestions.map((brand: any, index: number) => {
                    const parts = splitBrandText(brand.displayValue, query);
                    return (
                        <StyledWrapper {...styles.itemWrapper} key={brand.rawValue}>
                            <Link
                                {...styles.itemLink}
                                onClick={() => {
                                    onBrandClick(brand);
                                    closeAutocomplete();
                                }}
                            >
                                <Typography className="spanLink">
                                    {parts[0]}
                                    {parts[1]}
                                    {parts[2] && (
                                        <Typography {...styles.boldText}>
                                            {parts[2]}
                                        </Typography>
                                    )}
                                </Typography>
                            </Link>
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(CoveoSearchBrands);
